/**
 * Phase 3 — ComputeProfileSelector
 *
 * Three cards: Fast / Balanced / Rigorous. Each surfaces an
 * estimated wall-time + candidate count when the validate response
 * is available.
 */
import { Zap, Activity, Moon } from "lucide-react";

const PROFILES = [
    {
        id: "fast",
        label: "Fast",
        Icon: Zap,
        helper: "25–50 candidates · interactive · ~1–3 minutes",
        intent: "Smoke-test the design space; iterate on the composer.",
    },
    {
        id: "balanced",
        label: "Balanced",
        Icon: Activity,
        helper: "100–300 candidates · recommended · ~10–30 minutes",
        intent: "Production-quality search before promotion.",
        recommended: true,
    },
    {
        id: "rigorous",
        label: "Rigorous",
        Icon: Moon,
        helper: "500+ candidates · overnight · runs in the background",
        intent: "Pareto frontier + confirmation runs.",
        slow: true,
    },
];

export default function ComputeProfileSelector({
    value,
    onChange,
    estimate,
}) {
    return (
        <div
            className="grid grid-cols-1 md:grid-cols-3 gap-3"
            data-testid="profile-selector"
        >
            {PROFILES.map((p) => {
                const Icon = p.Icon;
                const active = value === p.id;
                return (
                    <button
                        key={p.id}
                        type="button"
                        onClick={() => onChange(p.id)}
                        data-testid={`profile-card-${p.id}`}
                        className={[
                            "text-left rounded-md border p-3 transition-all",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ux-accent",
                            active
                                ? "border-ux-accent bg-ux-accent-soft/40"
                                : "border-ux-border-1 bg-ux-bg-1 hover:bg-ux-bg-2/40",
                        ].join(" ")}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Icon
                                className={[
                                    "w-4 h-4",
                                    active ? "text-ux-accent" : "text-ux-text-3",
                                ].join(" ")}
                            />
                            <span className="text-[13px] font-medium text-ux-text-1">
                                {p.label}
                            </span>
                            {p.recommended && (
                                <span className="text-[10px] uppercase tracking-wider text-ux-success bg-ux-success-soft px-1.5 py-0.5 rounded">
                                    recommended
                                </span>
                            )}
                            {p.slow && (
                                <span className="text-[10px] uppercase tracking-wider text-ux-warning bg-ux-warning-soft px-1.5 py-0.5 rounded">
                                    overnight
                                </span>
                            )}
                        </div>
                        <p className="text-[11px] text-ux-text-3 mb-2">{p.helper}</p>
                        <p className="text-[11px] text-ux-text-2">{p.intent}</p>
                        {active && estimate && (
                            <div
                                className="mt-2 pt-2 border-t border-ux-border-1 text-[11px] text-ux-text-3 space-y-0.5"
                                data-testid={`profile-estimate-${p.id}`}
                            >
                                <p>
                                    <span className="text-ux-text-2">
                                        {estimate.estimated_candidates ?? "—"}
                                    </span>{" "}
                                    candidates
                                </p>
                                <p>
                                    <span className="text-ux-text-2">
                                        {estimate.estimated_baseline_runs ?? "—"}
                                    </span>{" "}
                                    baseline run ·{" "}
                                    <span className="text-ux-text-2">
                                        {estimate.estimated_confirmation_runs ?? 0}
                                    </span>{" "}
                                    confirmation runs
                                </p>
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
